import {
  type AvailableFilesQueryQuery,
  type AvailableFilesQueryQueryVariables,
} from '../../../generated/graphql';
import {AVAILABLE_FILES_QUERY} from '../../../graphql/media';
import {type Thumbnails, type TileMediaSpec} from '../../MediaCard';
import {getLastAvailableFilePathForTileMediaSpec} from './availableFiles';
import {getUnifiedUrlDataForFilePath, type UnifiedUrlData} from './batching';
import IN_MEMORY_CACHE, {setTrackingFlags} from './inMemoryCache';
import {
  getKeyForRunSignature,
  getKeyForTileMediaSpec,
  type RunSignatureKey,
  type TileMediaSpecKey,
} from './keys';
import {getRequestedPathSpec} from './paths';
import {type PathSpec} from './types';
import {updateWithNewData} from './update';
import {fetchGQL, groupTileMediaSpecsByRunSignatureKey} from './util';

type RunFilePathKey = `${RunSignatureKey}-${string}`;

type UrlMaps = {
  directUrls: Map<RunFilePathKey, string>;
  internalUrls: Map<RunFilePathKey, string>;
  thumbnails: Map<RunFilePathKey, Thumbnails>;
  hlsSrcs: Map<RunFilePathKey, string | null | undefined>;
};

function createUrlMaps(): UrlMaps {
  return {
    directUrls: new Map(),
    internalUrls: new Map(),
    thumbnails: new Map(),
    hlsSrcs: new Map(),
  };
}

function setUrlMapEntry(
  maps: UrlMaps,
  runKey: RunSignatureKey,
  filePath: string,
  urlData: UnifiedUrlData
): void {
  const mapKey: RunFilePathKey = `${runKey}-${filePath}`;
  if (urlData.directUrl != null) {
    maps.directUrls.set(mapKey, urlData.directUrl);
  }
  maps.internalUrls.set(mapKey, urlData.internalUrl);
  if (urlData.thumbnails != null) {
    maps.thumbnails.set(mapKey, urlData.thumbnails);
  }
  maps.hlsSrcs.set(mapKey, urlData.hlsSrc);
}

/**
 * The process for fetching new data is broken up into these primary steps:
 * 1. Take the tile media specs and group them by run signature key.
 * TileMediaSpec[] -> Map<string, TileMediaSpec[]>
 * 2. Get media file paths based on metadata (run, step, index)
 * 3. Get download URLs for media based on file paths
 */
export async function fetchNewTileMedia(
  tileMediaSpecs: TileMediaSpec[],
  attemptNonexistentSteps: boolean,
  disableVideoStream: boolean
): Promise<void> {
  // Record which query fields were requested for each spec upfront, before any async work.
  for (const spec of tileMediaSpecs) {
    setTrackingFlags(getKeyForTileMediaSpec(spec), {
      thumbnailsRequested: true,
      hlsSrcRequested: true,
    });
  }

  // STEP ONE: Group tile media specs by run signature key.
  // multiple media specs can share a single run signature key
  const tileMediaSpecsByRunSignatureKey =
    groupTileMediaSpecsByRunSignatureKey(tileMediaSpecs);

  /**
   * STEP 2: Get all the media file paths organized by the TileMediaSpecKey. Filter off all the media specs where the file upload may have started but not yet completed.
   */
  const existingFilePathByTileMediaSpecKey =
    await fetchExistingFilePathByKeyMap(
      tileMediaSpecsByRunSignatureKey,
      attemptNonexistentSteps
    );

  // STEP 3: AVAILABLE_FILES_QUERY already returned directUrl / thumbnails /
  // hlsSrc, so we build the URL maps from that side-channel data.
  const [
    directURLByRunFilePathOrArtifactURI,
    internalURLByRunFilePathOrArtifactURI,
    thumbnailsByRunFilePathOrArtifactURI,
    hlsSrcByRunFilePathOrArtifactURI,
  ] = buildUrlMapsFromUnifiedData(
    tileMediaSpecsByRunSignatureKey,
    existingFilePathByTileMediaSpecKey
  );

  // Download new media and update global cache used by the Media Panel to display media
  await updateWithNewData(
    tileMediaSpecsByRunSignatureKey,
    existingFilePathByTileMediaSpecKey,
    directURLByRunFilePathOrArtifactURI,
    internalURLByRunFilePathOrArtifactURI || new Map(),
    thumbnailsByRunFilePathOrArtifactURI || new Map(),
    hlsSrcByRunFilePathOrArtifactURI || new Map(),
    disableVideoStream
  );
}

type FilePathsByTileMediaSpecKey = Map<TileMediaSpecKey, PathSpec>;

/**
 * We fetch a mapping of tile -> existing file path. We can't just use the file path in the history without verifying its existence because there is a delay in uploading files, so the files specified by paths in history may not exist yet.
 */

async function fetchExistingFilePathByKeyMap(
  tileMediaSpecsByRunSignatureKey: Map<RunSignatureKey, TileMediaSpec[]>,
  attemptNonexistentSteps: boolean
) {
  const result: FilePathsByTileMediaSpecKey = new Map();

  const getExistingFilePathsPromises: Array<Promise<void>> = [];
  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    for (const tileMediaSpec of tileMediaSpecs) {
      getExistingFilePathsPromises.push(
        getExistingFilePath(tileMediaSpec, attemptNonexistentSteps, result)
      );
    }
  }
  await Promise.all(getExistingFilePathsPromises);

  return result;
}

async function getExistingFilePath(
  tileMediaSpec: TileMediaSpec,
  attemptNonexistentSteps: boolean,
  existingFilePathByKeyMap: FilePathsByTileMediaSpecKey
): Promise<void> {
  const stepNumber = tileMediaSpec.mediaStep;
  const existingFilePath = await getLastAvailableFilePathForTileMediaSpec({
    tileMediaSpec,
    attemptNonexistentSteps,
    stepNumber,
  });
  if (existingFilePath == null) {
    return;
  }

  const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
  existingFilePathByKeyMap.set(tileMediaSpecKey, existingFilePath);
}

/**
 * AVAILABLE_FILES_QUERY already returned directUrl / thumbnails / hlsSrc for
 * each file. Reads that data from the side-channel populated by
 * existingFilePaths.ts and builds the URL map structure that
 * updateWithNewData consumes.
 */
function buildUrlMapsFromUnifiedData(
  tileMediaSpecsByRunSignatureKey: Map<RunSignatureKey, TileMediaSpec[]>,
  existingFilePathByKey: Map<TileMediaSpecKey, PathSpec>
) {
  const maps = createUrlMaps();

  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    const {runSignature} = tileMediaSpecs[0];
    const runKey = getKeyForRunSignature(runSignature);

    for (const tileMediaSpec of tileMediaSpecs) {
      const pathSpec = existingFilePathByKey.get(
        getKeyForTileMediaSpec(tileMediaSpec)
      );
      if (pathSpec == null || !('filePath' in pathSpec)) {
        continue;
      }

      const {filePath} = pathSpec;
      const urlData = getUnifiedUrlDataForFilePath(runSignature, filePath);
      if (urlData?.directUrl == null) {
        continue;
      }

      setUrlMapEntry(maps, runKey, filePath, urlData);
    }
  }

  return [
    maps.directUrls,
    maps.internalUrls,
    maps.thumbnails,
    maps.hlsSrcs,
  ] as const;
}

/**
 * Used so the Media Panel can enable steps only when media is available.
 *
 * Flag off: `fetchNewTileMedia` + `TILE_MEDIA_BY_KEY` scan (unchanged at 0%).
 * Flag on: AvailableFiles existence query for the requested step only. Does
 * not walk earlier steps (MEDIA-2B) and does not write `IN_MEMORY_CACHE`.
 *
 * `shouldUseReactFetch` is passed from `MediaBrowserPanelWrapper` because this
 * helper is not a hook. Default false keeps existing 3-arg callers on the
 * cache path.
 */
export async function fetchMaxStepWithAvailableMedia(
  tileMediaSpecs: TileMediaSpec[],
  attemptNonexistentSteps: boolean,
  disableVideoStream: boolean,
  shouldUseReactFetch: boolean = false
): Promise<number | null> {
  if (shouldUseReactFetch) {
    return fetchMaxStepFromAvailableFilesQuery(tileMediaSpecs);
  }

  await fetchNewTileMedia(
    tileMediaSpecs,
    attemptNonexistentSteps,
    disableVideoStream
  );

  let maxStepWithAvailableMedia: number | null = null;
  for (const tileMediaSpec of tileMediaSpecs) {
    const key = getKeyForTileMediaSpec(tileMediaSpec);
    const exists = IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.has(key);
    if (
      exists &&
      (maxStepWithAvailableMedia == null ||
        tileMediaSpec.mediaStep > maxStepWithAvailableMedia)
    ) {
      maxStepWithAvailableMedia = tileMediaSpec.mediaStep;
    }
  }
  return maxStepWithAvailableMedia;
}

type SpecWithRequestedPath = {
  tileMediaSpec: TileMediaSpec;
  filePath: string;
};

/**
 * Same AvailableFiles document and include flags as `useFileData`. Groups by
 * run so one query covers every requested filename for that run.
 */
async function fetchMaxStepFromAvailableFilesQuery(
  tileMediaSpecs: TileMediaSpec[]
): Promise<number | null> {
  const specsWithPaths: SpecWithRequestedPath[] = [];
  for (const tileMediaSpec of tileMediaSpecs) {
    const pathSpec = getRequestedPathSpec(tileMediaSpec);
    if (pathSpec == null) {
      continue;
    }
    specsWithPaths.push({tileMediaSpec, filePath: pathSpec.filePath});
  }
  if (specsWithPaths.length === 0) {
    return null;
  }

  const specsByRunKey = new Map<RunSignatureKey, SpecWithRequestedPath[]>();
  for (const specWithPath of specsWithPaths) {
    const runKey = getKeyForRunSignature(
      specWithPath.tileMediaSpec.runSignature
    );
    const specsForRun = specsByRunKey.get(runKey) ?? [];
    specsForRun.push(specWithPath);
    specsByRunKey.set(runKey, specsForRun);
  }

  const availableSteps: number[] = [];
  await Promise.all(
    [...specsByRunKey.values()].map(async specsForRun => {
      const first = specsForRun[0];
      if (first == null) {
        return;
      }
      const {runSignature} = first.tileMediaSpec;
      const filenames = [...new Set(specsForRun.map(s => s.filePath))];
      const data = await fetchGQL<
        AvailableFilesQueryQueryVariables,
        AvailableFilesQueryQuery
      >({
        query: AVAILABLE_FILES_QUERY,
        variables: {
          entityName: runSignature.entityName,
          projectName: runSignature.projectName,
          runName: runSignature.runName,
          filenames,
          includeDirectUrl: true,
          includeThumbnails: true,
          includeHlsSrc: true,
        },
      });
      const existingNames = existingFileNamesFromAvailableFiles(data);
      for (const {tileMediaSpec, filePath} of specsForRun) {
        if (existingNames.has(filePath)) {
          availableSteps.push(tileMediaSpec.mediaStep);
        }
      }
    })
  );

  if (availableSteps.length === 0) {
    return null;
  }
  return Math.max(...availableSteps);
}

function existingFileNamesFromAvailableFiles(
  data: AvailableFilesQueryQuery | null
): Set<string> {
  const names = new Set<string>();
  if (data == null) {
    return names;
  }
  for (const edge of data.project?.run?.files?.edges ?? []) {
    const node = edge.node;
    if (node != null && Number(node.sizeBytes) > 0) {
      names.add(node.name);
    }
  }
  return names;
}
